// pages/Community/CommunityAutomation.jsx
//
// The "Automation" tab (Phase 4). Consent-gated broadcasts: compose a message
// and send it to a brand's OPTED-IN contacts on a channel. WhatsApp requires an
// approved template (Meta rule); other channels send plain text. Never targets
// non-opted-in contacts, so it can't spam.

import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import addNotification from "helpers/notify";
import { getCampaigns, sendCampaign } from "api/messaging/messaging";

const CHANNELS = ["whatsapp", "telegram", "discord", "slack", "teams"];

const CommunityAutomation = ({ brandId: brandIdProp }) => {
  const brandsRedux = useSelector((s) => s.user.brands) || [];
  const [brandId, setBrandId] = useState(brandIdProp || "");
  const [channel, setChannel] = useState("whatsapp");
  const [text, setText] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  const selectedBrand = brandsRedux.find((b) => String(b._id) === String(brandId));
  const brandSettingsId = selectedBrand && selectedBrand.brandSettings && selectedBrand.brandSettings._id;

  const loadHistory = useCallback(async () => {
    if (!brandId) { setHistory([]); return; }
    try { const res = await getCampaigns({ brandId }); setHistory((res && res.data) || []); } catch (e) { /* ignore */ }
  }, [brandId]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const send = useCallback(async () => {
    if (!text.trim()) { addNotification("Message required.", "danger"); return; }
    if (channel === "whatsapp" && !templateName.trim()) { addNotification("WhatsApp campaigns need an approved template name.", "danger"); return; }
    if (!window.confirm(`Send this ${channel} message to all opted-in contacts for this brand?`)) return;
    setBusy(true);
    try {
      const res = await sendCampaign({ brandId, brandSettingsId, channel, text: text.trim(), templateName: templateName.trim() || undefined });
      if (res && res.success) { addNotification(`Sent to ${res.sent}/${res.audience} (${res.failed} failed).`, "success"); setText(""); loadHistory(); }
      else addNotification(`Campaign failed: ${(res && res.error) || "no opted-in contacts"}`, "danger");
    } catch (e) { addNotification((e && e.message) || "Campaign failed.", "danger"); }
    finally { setBusy(false); }
  }, [text, channel, templateName, brandId, brandSettingsId, loadHistory]);

  return (
    <div style={{ maxWidth: 720 }}>
      <h5 style={{ fontWeight: 700 }}>Automation</h5>
      <p className="text-muted" style={{ fontSize: "0.85rem" }}>
        Broadcast to a brand's <strong>opted-in</strong> contacts. WhatsApp needs an approved
        template; Telegram/Discord/Slack/Teams send plain text. Non-opted-in contacts are never messaged.
      </p>

      <div className="d-flex mb-2" style={{ gap: 8 }}>
        <select className="form-control form-control-sm" style={{ maxWidth: 240 }} value={brandId} onChange={(e) => setBrandId(e.target.value)}>
          <option value="">Select a brand…</option>
          {brandsRedux.map((b) => <option key={b._id} value={b._id}>{b.name}</option>)}
        </select>
        <select className="form-control form-control-sm" style={{ maxWidth: 160 }} value={channel} onChange={(e) => setChannel(e.target.value)}>
          {CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {channel === "whatsapp" ? (
        <input className="form-control form-control-sm mb-2" placeholder="Approved WhatsApp template name (e.g. promo_update)" value={templateName} onChange={(e) => setTemplateName(e.target.value)} />
      ) : null}
      <textarea className="form-control form-control-sm mb-2" rows={4} placeholder="Your message…" value={text} onChange={(e) => setText(e.target.value)} />
      <button className="btn btn-sm btn-primary m-0" style={{ backgroundColor: "#0b3d91", borderColor: "#0b3d91" }} disabled={busy || !brandId} onClick={send}>
        {busy ? "Sending…" : "Send to opted-in contacts"}
      </button>

      {history.length ? (
        <div style={{ marginTop: 20 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Recent campaigns</div>
          <table className="table table-sm" style={{ fontSize: "0.8rem" }}>
            <thead><tr><th>When</th><th>Channel</th><th>Audience</th><th>Sent</th><th>Failed</th><th>Message</th></tr></thead>
            <tbody>
              {history.map((c) => (
                <tr key={c._id}>
                  <td>{new Date(c.createdAt).toLocaleString()}</td>
                  <td>{c.channel}</td>
                  <td>{c.audienceCount}</td>
                  <td className="text-success">{c.sentCount}</td>
                  <td className={c.failedCount ? "text-danger" : ""}>{c.failedCount}</td>
                  <td className="text-truncate" style={{ maxWidth: 220 }}>{c.text}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
};

export default CommunityAutomation;
